<template>
  <modal :open="internalOpen" :width="1000">
    <template #header>
      <modal-header
        variant="buttons"
        :saving="loading"
        @cancel="closeModal()"
        @submit="save"
      >
        <h1>{{ $t(`pages.segmentedDrivers.modal.edit.title`) }}</h1>
      </modal-header>
    </template>

    <form @submit="save">
      <div class="mt-8">
        <div class="flex flex-col w-full">
          <form-card
            :title="$t(`pages.segmentedDrivers.modal.edit.general.title`)"
            :description="$t(`pages.segmentedDrivers.modal.edit.general.description`)"
          >
            <div class="flex flex-col gap-y-5">
              <Multiselect
                v-model="internalSegmentation.region"
                :error="errors?.region"
                :options="regions"
                :label="$t('mounting.region')"
                class="w-full"
                :can-deselect="false"
                :can-clear="false"
                :searchable="true"
                @select="updateRegion()"
              />
              <small v-if="!loadingLastCampaign">{{ t('pages.segmentedDrivers.modal.edit.hintLastCampaign') + lastCampaignInRegion }}</small>
              <small v-else>{{ t('pages.segmentedDrivers.modal.edit.loadingLastCampaign') }}</small>
              <LocationMap
                ref="map"
                v-model="internalSegmentation.gisLocation"
                v-model:radius="internalSegmentation.radius"
              />
              <div class="flex gap-x-5">
                <FormInput
                  :model-value="internalSegmentation.gisLocation?.lat"
                  :error="errors?.gisLocation?.lat"
                  class="w-1/2"
                  type="text"
                  :label="$t('lat')"
                  pattern="^\d*\.?\d*$"
                  @update:model-value="setCoords($event, internalSegmentation.gisLocation?.long)"
                />
                <FormInput
                  :model-value="internalSegmentation.gisLocation?.long"
                  :error="errors?.gisLocation?.long"
                  class="w-1/2"
                  type="text"
                  :label="$t('long')"
                  pattern="^\d*\.?\d*$"
                  @update:model-value="setCoords(internalSegmentation.gisLocation?.lat, $event)"
                />
              </div>
              <FormInput
                v-model="internalSegmentation.radius"
                :error="errors?.radius"
                class="col-start-1 col-end-2"
                type="number"
                :label="$t('segmentation.radius')"
                :placeholder="$t('segmentation.radius')"
              />
              <div class="flex gap-x-5 mb-5">
                <DatePicker
                  v-model="internalSegmentation.startDate"
                  :error="errors?.startDate"
                  mode="datetime"
                  class="w-full"
                  :label="$t('segmentation.startDate')"
                />
                <DatePicker
                  v-model="internalSegmentation.endDate"
                  :error="errors?.endDate"
                  mode="datetime"
                  class="w-full"
                  :label="$t('segmentation.endDate')"
                />
              </div>
            </div>
          </form-card>
        </div>
      </div>
    </form>
  </modal>
</template>

<script lang="ts" setup>
import {
  computed, onMounted, reactive, ref, unref,
} from 'vue';
import { useVModel } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { useValidation } from '@hooks/form';
import {
  date, number, object, string,
} from 'yup';
import Modal from '@utils/Modal/Modal.vue';
import Multiselect from '@utils/Multiselect.vue';
import DatePicker from '@components/utils/inputs/DatePicker.vue';
import FormInput from '@utils/inputs/Input.vue';
import { useNotify } from '@utils/notifications';
import { useFetchAction } from '@hooks/fetch';
import ModalHeader from '@utils/Modal/ModalHeader.vue';
import FormCard from '@utils/FormCard.vue';
import LocationMap from '@utils/maps/PickLocationRadiusMap.vue';
import {
  collOptionsProvider,
  useProvider,
} from '@utils/table/hooks/provider';
import { useRoute } from 'vue-router';
import { useQueryRef } from '@hooks/url';
import { format } from 'date-fns';
import type {
  Campaign, HydraCollection, Region,
} from '@/entities/Entities';

interface Props {
  open: boolean;
}

const emit = defineEmits(['update:open', 'saved']);
const props = defineProps<Props>();
const internalOpen = useVModel(props, 'open', emit);

const now = new Date();
const sixtyDaysAgo = new Date(now);
sixtyDaysAgo.setDate(now.getDate() - 60);

const internalSegmentation = reactive({
  gisLocation: { lat: 0, long: 0 },
  region: undefined,
  radius: 20,
  startDate: sixtyDaysAgo,
  endDate: now,
});

const { t } = useI18n();
const notify = useNotify();

const fetchRegions = collOptionsProvider<Region>(
  '/regions',
  {
    sort: {
      sortKey: 'name',
      sortDirection: 'ASC',
    },
  },
  (value) => ({
    label: value.name,
    value: value['@id'],
    gisLocation: value.gisLocation,
  }),
);
const regions = ref<
  { label: string; value: string | null; gisLocation?: { lat: number; long: number } }[]
>([]);
const loadRegions = async () => {
  regions.value = await fetchRegions();
};
loadRegions();

const {
  data: segmentation,
  errors,
  validatedFn,
} = useValidation(
  object({
    region: string().nullable().required(t('validation.notEmpty')),
    gisLocation: object({
      lat: number().typeError(t('validation.number')).nullable().required(t('validation.notEmpty')),
      long: number()
        .typeError(t('validation.number'))
        .nullable()
        .required(t('validation.notEmpty')),
    }),
    radius: number()
      .typeError(t('validation.number'))
      .positive(t('validation.positive'))
      .integer(t('validation.integer'))
      .nullable()
      .required(t('validation.notEmpty')),
    startDate: date().nullable()
      .required(t('validation.notEmpty'))
      .test(
        'start-after-campaign',
        t('pages.segmentedDrivers.modal.edit.validation.startAfterEnd'),
        (start) => {
          if (!start) return false;
          return new Date(start) <= new Date(internalSegmentation.endDate);
        },
      )
      .test(
        'duration-not-over-2-months',
        t('pages.segmentedDrivers.modal.edit.validation.duration'),
        (start) => {
          if (!start || !internalSegmentation.endDate) return false;

          const startDate = new Date(start);
          const endDate = new Date(internalSegmentation.endDate);

          const maxDuration = 1000 * 60 * 60 * 24 * 70; // 2 Months

          return endDate.getTime() - startDate.getTime() <= maxDuration;
        },
      ),
    endDate: date().nullable()
      .required(t('validation.notEmpty')).test(
        'end-after-start',
        t('pages.segmentedDrivers.modal.edit.validation.endAfterStart'),
        (end) => {
          if (!end) return false;
          return new Date(end) >= new Date(internalSegmentation.startDate);
        },
      ),
  }),
  internalSegmentation,
);

function closeModal() {
  internalOpen.value = false;
}

const page = useQueryRef('page', 1);
const perPage = useQueryRef('perPage', 1);
const fixedFilter = computed(() => ({ region: internalSegmentation.region ?? '' }));
const totalFilter = computed(() => ({ ...fixedFilter.value }));
const sortKey = useQueryRef<string | undefined>('sortKey', 'actualEndDate');
const sortDirection = useQueryRef<'ASC' | 'DESC' | undefined>('sortDirection', 'DESC');

const providerParams = reactive({
  sort: {
    sortKey,
    sortDirection,
  },
  filter: totalFilter,
  pagination: {
    page,
    perPage,
  },
});

const {
  data: items,
  loading: loadingLastCampaign,
} = useProvider<HydraCollection<Campaign>>('/campaigns', providerParams, {
  immediate: false,
});
const records = computed(() => unref(items)?.['hydra:member']);
const lastCampaignInRegion = computed(() => (records.value?.[0]?.actualEndDate ? format(new Date(records.value?.[0]?.actualEndDate), 'dd.MM.yyyy') : 'N/A'));
const map = ref();

const setMapCenter = () => {
  const gisLocation = regions.value.find(
    (region) => region.value === internalSegmentation?.region,
  )?.gisLocation;
  map.value?.moveToCenter(gisLocation?.lat, gisLocation?.long);
};

const updateRegion = () => {
  setMapCenter();
};

const setCoords = (lat: number | undefined, long: number | undefined) => {
  debugger;
  internalSegmentation.gisLocation = {
    lat: lat ?? internalSegmentation.gisLocation?.lat ?? 0,
    long: long ?? internalSegmentation.gisLocation?.long ?? 0,
  };
};

const [, { loading }] = useFetchAction();

const route = useRoute();
onMounted(() => {
  if (route.query.segmentation) {
    try {
      // parse segmentation from URL if present
      const parsed = JSON.parse(decodeURIComponent(String(route.query.segmentation)));
      Object.assign(internalSegmentation, {
        region: parsed.region,
        radius: Number(parsed.radius),
        startDate: parsed.startDate ? new Date(parsed.startDate) : undefined,
        endDate: parsed.endDate ? new Date(parsed.endDate) : undefined,
        gisLocation: {
          lat: Number(parsed.lat),
          long: Number(parsed.long),
        },
      });
      console.log(parsed);
    } catch (e) {
      console.warn('Failed to parse segmentation from URL', e);
    }
  }
});

const save = validatedFn(async () => {
  try {
    const queryParams = {
      region: segmentation.value?.region ?? '',
      radius: segmentation.value.radius?.toString() ?? '',
      startDate: segmentation.value.startDate?.toISOString() ?? '',
      endDate: segmentation.value.endDate?.toISOString() ?? '',
      lat: segmentation.value.gisLocation?.lat?.toString() ?? '',
      long: segmentation.value.gisLocation?.long?.toString() ?? '',
    };

    emit('saved', queryParams);

    notify?.({
      variant: 'success',
      title: t('pages.segmentedDrivers.modal.edit.saveSuccess'),
    });

    closeModal();
  } catch (e) {
    notify?.({
      variant: 'error',
      title: t('pages.segmentedDrivers.modal.edit.saveError'),
    });
  }
});
</script>

<style lang="scss" scoped></style>
